import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import Preloader from "@/components/Preloader";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Work from "@/components/Work";
import GallerySection from "@/components/GallerySection";
import Services from "@/components/Services";
import Manifesto from "@/components/Manifesto";
import About from "@/components/About";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import GalleryPage from "@/pages/GalleryPage";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-ink flex items-center justify-center font-mono text-sm text-smoke">
          Something went wrong. Please refresh the page.
        </div>
      );
    }
    return this.props.children;
  }
}

const scrollToSection = (target) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(target, { offset: 0, duration: 1.4 });
  } else {
    document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
  }
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Site() {
  const [loading, setLoading] = useState(true);
  const [projectIntent, setProjectIntent] = useState(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = loading ? "hidden" : "";
  }, [loading]);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="relative bg-ink text-white font-body">
        <AnimatePresence>
          {loading && <Preloader key="preloader" onDone={() => setLoading(false)} />}
        </AnimatePresence>

        <Navbar />

        <Routes>
          <Route
            path="/"
            element={
              <main>
                <Hero active={!loading} onNavigate={scrollToSection} />
                <Marquee />
                <Work />
                <GallerySection />
                <Services
                  onDiscuss={(type) => {
                    setProjectIntent(type);
                    scrollToSection("#contact");
                  }}
                />
                <Manifesto />
                <About />
                <Contact intent={projectIntent} />
              </main>
            }
          />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <Footer />
        <Toaster theme="dark" position="bottom-right" />
      </div>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <Site />
    </ErrorBoundary>
  );
}
